import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, Loader2 } from 'lucide-react';
import { authAPI } from '../../services/api';
import toast from '../../services/toast';
import { AuthSplitShell } from '../../components/auth/AuthSplitShell';
import Input from '../../components/ui/Input';
import {
  authLabelClass,
  authPrimaryButton,
  authFooterClass,
  authFooterLink,
} from '../../components/auth/authViewTheme';

const GENERIC_SUCCESS =
  "If an account exists with that email, you'll receive a reset link shortly.";

const ForgotPassword = () => {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { email: '' } });

  const onSubmit = handleSubmit(async ({ email }) => {
    setSubmitted(false);
    try {
      await authAPI.forgotPassword(email);
      setSubmitted(true);
      toast.success(GENERIC_SUCCESS);
      reset({ email: '' });
    } catch (err) {
      const msg = (err as Error).message || 'Request failed. Please try again.';
      if (msg.includes('not available') || msg.includes('not configured')) {
        toast.error('Password reset is not available. Please contact your administrator.');
      } else if ((err as Error).message && ((err as Error).message.includes('Too many') || (err as Error).message.includes('429'))) {
        toast.error('Too many requests. Try again later.');
      } else {
        toast.error(msg);
      }
    }
  });

  return (
    <AuthSplitShell
      title="Forgot password"
      description="Enter your email and we'll send you a reset link"
    >
      {submitted ? (
        <p className="text-center text-sm text-slate-400">{GENERIC_SUCCESS}</p>
      ) : (
        <form className="space-y-5" onSubmit={onSubmit} noValidate>
          <div>
            <label htmlFor="forgot-email" className={authLabelClass}>
              Email
            </label>
            <Input
              id="forgot-email"
              type="email"
              autoComplete="email"
              placeholder="Enter your email"
              {...register('email', { required: true })}
            />
          </div>
          <button type="submit" disabled={isSubmitting} className={authPrimaryButton}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Sending…
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 shrink-0" aria-hidden />
                Send reset link
              </>
            )}
          </button>
        </form>
      )}
      <p className={authFooterClass}>
        <Link to="/login" className={authFooterLink}>
          Back to sign in
        </Link>
      </p>
    </AuthSplitShell>
  );
};

export default ForgotPassword;
